// src/Pages/Newpatientsappointment.jsx
//
// Notes for a booking that has no patient record yet (status "New Patient").
// The notes save against the appointment with patient "1"; once the patient
// and account exist, Link Notes on the patient file moves them across.
//
// Reached from the calendar's "Go To Session" on a New Patient appointment.
// It renders the same Notes section as the patient file, in its
// unregistered mode: the list is this appointment's notes, and SyncTo and
// the invoice hand-off are off because there is no patient to file against.
import React, { useEffect, useMemo, useState } from "react";
import { Alert, Col, Container, Row } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { jwtDecode } from "jwt-decode";

import { getAppointmentById, getServiceById } from "../api";
import { Breadcrumb } from "../Components/Breadcrumb/Breadcrumb";
import { getBookingsReturnPath } from "../utils/bookingsReturn";
import PatientNotes from "../Components/Sessions/PatientNotes";
import "../Components/Accounts/acc-list.css";

const formatBreadcrumbDate = (dateString) => {
  if (!dateString) return "";
  return new Date(dateString).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

export function NewPatientsappointment() {
  const { appointmentId } = useParams();
  const [appointment, setAppointment] = useState(null);
  const [service, setService] = useState(null);

  const userRole = useMemo(() => {
    try {
      const token = sessionStorage.getItem("user");
      const decoded = token ? jwtDecode(token) : null;
      const roles = Array.isArray(decoded?.role) ? decoded.role : [];
      return roles.includes("Practitioner") || roles.includes("SuperAdmin");
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    if (!appointmentId) return;
    let cancelled = false;
    (async () => {
      try {
        const appt = await getAppointmentById(appointmentId);
        if (cancelled) return;
        setAppointment(appt);
        if (appt?.serviceId) {
          const svc = await getServiceById(appt.serviceId);
          if (!cancelled) setService(svc);
        }
      } catch (e) {
        console.error("Error fetching appointment:", e);
      }
    })();
    return () => { cancelled = true; };
  }, [appointmentId]);

  const name = [appointment?.patientFirstName, appointment?.patientLastName]
    .filter(Boolean)
    .join(" ")
    .trim();

  const breadcrumbItems = appointment
    ? [
        { label: "Appointments", path: getBookingsReturnPath() },
        { label: `${formatBreadcrumbDate(appointment.start)}, ${name || "New patient"}` },
      ]
    : null;

  return (
    <div style={{ marginTop: "50px" }}>
      <Container fluid className="pt-3 pb-3">
        {breadcrumbItems && (
          <div className="ms-1 mb-2">
            <Breadcrumb items={breadcrumbItems} />
          </div>
        )}

        <div className="ms-1 mb-3">
          <h5 className="text-primary fw-semibold mb-0">{name || "New patient"}</h5>
        </div>

        <div className="acc-list mb-3">
          <Row className="gx-3" style={{ fontSize: "0.875rem" }}>
            <Col xs={12} md={6}>
              <h6 className="text-primary fw-bold mb-2">Booking details</h6>
              {appointment?.patientEmail && (
                <div className="mb-1">
                  <i className="fa-light fa-envelope me-2 text-primary" />
                  {appointment.patientEmail}
                </div>
              )}
              {appointment?.patientPhone && (
                <div className="mb-1">
                  <i className="fa-light fa-phone me-2 text-primary" />
                  {appointment.patientPhone}
                </div>
              )}
              {!appointment && <p className="mb-0">Loading booking…</p>}
            </Col>
            <Col xs={12} md={6}>
              <Alert variant="warning" className="mb-0">
                This booking has <strong>no patient record yet</strong>. Notes are saved on the
                appointment and move to the patient once the patient and account are created,
                via Link Notes on the patient file.
              </Alert>
            </Col>
          </Row>
        </div>

        {!userRole && (
          <div className="acc-list">
            <p className="mb-0">Notes on this booking are available to practitioners.</p>
          </div>
        )}
        {userRole && (
          <PatientNotes
            patientId="1"
            appointmentId={appointmentId}
            appointment={appointment}
            service={service}
            patientLabel={name}
            practiceId={appointment?.practiceId || null}
          />
        )}
      </Container>
    </div>
  );
}
